import { Box, Divider, Paper, Typography } from '@mui/material'
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter'
import WhatshotIcon from '@mui/icons-material/Whatshot'
import LocalCafeIcon from '@mui/icons-material/LocalCafe'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import TimerIcon from '@mui/icons-material/Timer'

const GOLD = '#d3b908'
const GOLD_HOVER = '#FDE025'

const passes = [
  {
    title: 'Gym Day Pass',
    icons: [FitnessCenterIcon],
    lines: ['Full access to', 'the gym / class pass'],
    price: '£8',
  },
  {
    title: 'Hyve Plus',
    icons: [FitnessCenterIcon, WhatshotIcon, LocalCafeIcon],
    lines: ['Gym / class pass', 'Red light sauna session', 'Coffee of choice'],
    price: '£20',
  },
]

const footerItems = [
  { Icon: CalendarMonthIcon, label: 'Valid all day' },
  { Icon: null, label: 'No commitment' },
  { Icon: TimerIcon, label: 'Train on your time' },
]

/**
 * Informational, poster-styled presentation of the gym's walk-in day pass
 * options. Purely visual — no checkout is wired up for day passes.
 */
function DayPasses() {
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 900,
        mx: 'auto',
        mb: 4,
        px: { xs: 1, sm: 0 },
      }}
    >
      <Typography
        variant='h4'
        sx={{
          fontFamily: 'Oswald',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '2px',
          textAlign: 'center',
          color: 'white.main',
        }}
      >
        Day Pass Options
      </Typography>
      <Typography
        variant='body2'
        sx={{ textAlign: 'center', color: '#c1c1c1', mt: 0.5, mb: 3 }}
      >
        Not ready for a membership? Walk in and train today.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        {passes.map(({ title, icons, lines, price }) => (
          <Paper
            key={title}
            elevation={0}
            sx={{
              bgcolor: 'rgba(0, 0, 0, 0.55)',
              border: `1px solid ${GOLD}`,
              borderRadius: 2,
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              transition:
                'transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                borderColor: GOLD_HOVER,
                boxShadow: `0 0 24px rgba(211, 185, 8, 0.35)`,
              },
            }}
          >
            <Typography
              variant='h6'
              sx={{
                fontFamily: 'Oswald',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                color: 'white.main',
              }}
            >
              {title}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                justifyContent: 'center',
                my: 2,
              }}
            >
              {icons.map((Icon, i) => (
                <Icon key={i} sx={{ fontSize: 40, color: GOLD }} />
              ))}
            </Box>

            <Box sx={{ minHeight: 72 }}>
              {lines.map((line) => (
                <Typography
                  key={line}
                  variant='body2'
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'white.main',
                  }}
                >
                  {line}
                </Typography>
              ))}
            </Box>

            <Typography
              sx={{
                fontFamily: 'Oswald',
                fontWeight: 700,
                fontSize: { xs: '2.5rem', sm: '3rem' },
                lineHeight: 1,
                color: GOLD,
                mt: 1.5,
              }}
            >
              {price}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Box
        sx={{
          mt: 3,
          border: `1px solid ${GOLD}`,
          borderRadius: 2,
          py: 1.5,
          px: 2,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 3 },
        }}
      >
        {footerItems.map(({ Icon, label }, i) => (
          <Box
            key={label}
            sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
          >
            {i > 0 && (
              <Divider
                orientation='vertical'
                flexItem
                sx={{
                  borderColor: GOLD,
                  display: { xs: 'none', sm: 'block' },
                }}
              />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {Icon && <Icon sx={{ fontSize: 22, color: GOLD }} />}
              <Typography
                variant='body2'
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 600,
                  color: 'white.main',
                }}
              >
                {label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Typography
        variant='caption'
        sx={{
          display: 'block',
          textAlign: 'center',
          color: '#c1c1c1',
          mt: 1.5,
        }}
      >
        Just turn up during our manned hours and pay at the door.
      </Typography>
    </Box>
  )
}

export default DayPasses
